/* Push im Browser (Phase 6): Zustand, Einschalten, Ausschalten. Auf dem iPhone geht Push nur in der installierten App (ab iOS 16.4). */
import { db } from "./supabase";

export type PushZustand = "nicht_unterstuetzt" | "ios_installieren" | "abgelehnt" | "aus" | "an";

export function istIos(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

export function istInstalliert(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches === true || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function unterstuetzt(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function pushZustand(): Promise<{ zustand: PushZustand; endpoint: string | null }> {
  if (!unterstuetzt()) return { zustand: istIos() && !istInstalliert() ? "ios_installieren" : "nicht_unterstuetzt", endpoint: null };
  if (Notification.permission === "denied") return { zustand: "abgelehnt", endpoint: null };
  const reg = await navigator.serviceWorker.getRegistration("/");
  const abo = await reg?.pushManager.getSubscription();
  return { zustand: abo && Notification.permission === "granted" ? "an" : "aus", endpoint: abo?.endpoint ?? null };
}

/** Kurzer Gerätename ohne Details aus dem User-Agent (für die Liste „Deine Geräte“). */
export function geraetName(ua = typeof navigator === "undefined" ? "" : navigator.userAgent): string {
  const system = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Gerät";
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "";
  return browser ? `${system}, ${browser}` : system;
}

export function schluesselBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const roh = atob(b64);
  const bytes = new Uint8Array(new ArrayBuffer(roh.length));
  for (let i = 0; i < roh.length; i++) bytes[i] = roh.charCodeAt(i);
  return bytes;
}

const fehler = (meldung: string) => Object.assign(new Error(meldung), { code: "P0001" });

/** Push auf diesem Gerät einschalten. Muss direkt aus einem Klick kommen (Erlaubnis im Browser). */
export async function pushEinschalten(): Promise<void> {
  const erlaubnis = await Notification.requestPermission();
  if (erlaubnis === "denied") throw fehler("Push ist abgelehnt. In den Einstellungen des Browsers kannst du es wieder erlauben.");
  if (erlaubnis !== "granted") throw fehler("Ohne Erlaubnis gibt es keine Push-Mitteilungen.");
  const res = await fetch("/.netlify/functions/push-schluessel");
  const j = (await res.json().catch(() => ({}))) as { ok?: boolean; key?: string; fehler?: string };
  if (!res.ok || !j.key) throw fehler(j.fehler ?? "Push ist hier noch nicht eingerichtet.");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const abo = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: schluesselBytes(j.key) }));
  const keys = abo.toJSON().keys ?? {};
  const { error } = await db().rpc("app_push_abo_speichern", { p_endpoint: abo.endpoint, p_p256dh: keys.p256dh ?? "", p_auth: keys.auth ?? "", p_geraet: geraetName() });
  if (error) throw error;
}

/** Ein Gerät entfernen. Ist es dieses Gerät, wird auch das Abo im Browser beendet. */
export async function pushAusschalten(id: number, endpoint: string): Promise<void> {
  if (unterstuetzt()) {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const abo = await reg?.pushManager.getSubscription();
    if (abo && abo.endpoint === endpoint) await abo.unsubscribe();
  }
  const { error } = await db().rpc("app_push_abo_entfernen", { p_id: id });
  if (error) throw error;
}

export async function pushTesten(): Promise<number> {
  const { data } = await db().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw fehler("Bitte melde dich neu an.");
  const res = await fetch("/.netlify/functions/push-test", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
  const j = (await res.json().catch(() => ({}))) as { ok?: boolean; geraete?: number; fehler?: string };
  if (!res.ok || !j.ok) throw fehler(j.fehler ?? "Der Test hat nicht geklappt.");
  return j.geraete ?? 0;
}

export function serviceWorkerAnmelden(): void {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

function base64url(bytes: Uint8Array): string {
  let roh = "";
  for (const b of bytes) roh += String.fromCharCode(b);
  return btoa(roh).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** VAPID-Schlüsselpaar im Browser (Web Crypto, P-256), im Format, das web-push erwartet: öffentlich 65 Bytes, privat 32 Bytes, beides base64url.
 *  Für die Seite „Push-Schlüssel erzeugen“ (nur Admins). Nichts davon wird gespeichert oder gesendet. */
export async function vapidPaar(quelle: SubtleCrypto = crypto.subtle): Promise<{ oeffentlich: string; privat: string }> {
  const paar = await quelle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"]);
  const roh = new Uint8Array(await quelle.exportKey("raw", paar.publicKey));
  const jwk = await quelle.exportKey("jwk", paar.privateKey);
  if (!jwk.d) throw fehler("Der Browser hat keinen privaten Schlüssel geliefert.");
  return { oeffentlich: base64url(roh), privat: jwk.d };
}
